/**
 * StatusKpis — banda de KPIs por estado del día para el Centro de Control.
 * A diferencia del KpiBand compartido (Torre admin), separa los estados en
 * tiles propios: Pendientes (status 0) → Recolectados → En ruta → Entregados →
 * Fallidos, más Nivel de servicio (con aro) y Repartidores activos.
 *
 * Datos de getPackagesByStatusCount (packages-resume) + operational-summary,
 * scoped al logisticCompanyId del display.
 */
import { useTranslation } from 'react-i18next';
import { Inbox, PackageCheck, Truck, CheckCircle2, XCircle, Target, Users } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Skeleton } from '../../../components/ui/Skeleton';
import { formatNumberCL } from '../../../utils/formatters';

const TONE = {
  brand: 'bg-brand-50 text-brand-700',
  info: 'bg-status-info-soft text-status-info',
  warning: 'bg-status-warning-soft text-status-warning',
  success: 'bg-status-success-soft text-status-success',
  error: 'bg-status-error-soft text-status-error',
  neutral: 'bg-bg-muted text-fg-muted',
};

function slColor(pct) {
  if (pct >= 90) return 'rgb(var(--status-success))';
  if (pct >= 80) return 'rgb(var(--brand-500))';
  if (pct >= 70) return 'rgb(var(--status-warning))';
  return 'rgb(var(--status-error))';
}

function Tile({ icon: Icon, tone, value, label, sub, loading }) {
  return (
    <div className="flex items-center gap-3 px-3 py-3 md:px-4 min-w-0">
      <span
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 [&>svg]:w-5 [&>svg]:h-5',
          TONE[tone] || TONE.neutral,
        )}
      >
        <Icon strokeWidth={2.1} />
      </span>
      <div className="min-w-0">
        <div className="text-[22px] font-bold tabular-nums text-fg leading-none">
          {loading ? <Skeleton className="h-6 w-12" /> : value}
        </div>
        <div className="text-[11px] uppercase tracking-wide font-semibold text-fg-muted mt-1 truncate">
          {label}
        </div>
        {sub && !loading && <div className="text-[11px] text-fg-subtle mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

export default function StatusKpis({ statusCounts, kpis, activeDrivers, totalDrivers, loading }) {
  const { t } = useTranslation();
  const c = statusCounts || {};
  const sl = kpis?.serviceLevelPct ?? 0;
  const total = kpis?.total ?? 0;

  return (
    <div className="rounded-xl border border-border bg-bg shadow-card overflow-hidden">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 divide-x divide-y xl:divide-y-0 divide-border">
        <Tile
          icon={Inbox}
          tone="neutral"
          loading={loading}
          value={formatNumberCL(c.pendiente || 0)}
          label={t('Pendientes')}
          sub={t('creados, sin recolectar')}
        />
        <Tile
          icon={PackageCheck}
          tone="info"
          loading={loading}
          value={formatNumberCL(c.recolectado || 0)}
          label={t('Recolectados')}
          sub={t('en hub / por despachar')}
        />
        <Tile
          icon={Truck}
          tone="warning"
          loading={loading}
          value={formatNumberCL(c.ruta || 0)}
          label={t('En ruta')}
          sub={t('en reparto')}
        />
        <Tile
          icon={CheckCircle2}
          tone="success"
          loading={loading}
          value={
            // null = el backend no pudo contar las entregas: "sin dato", no 0. Sin
            // conteos en absoluto `c` llega vacío y queda el 0, como en los demás tiles.
            c.entregado === null ? '—' : formatNumberCL(c.entregado || 0)
          }
          label={t('Entregados')}
          sub={
            c.entregado === null
              ? undefined
              : `${formatNumberCL(c.entregado || 0)} ${t('de')} ${formatNumberCL(total)}`
          }
        />
        <Tile
          icon={XCircle}
          tone="error"
          loading={loading}
          value={formatNumberCL(c.fallido || 0)}
          label={t('Fallidos hoy')}
          sub={t('requieren reintento')}
        />

        {/* Nivel de servicio con aro de progreso */}
        <div className="flex items-center gap-3 px-3 py-3 md:px-4 min-w-0">
          {loading ? (
            <Skeleton className="w-11 h-11 rounded-full shrink-0" />
          ) : (
            <span
              className="relative w-11 h-11 rounded-full shrink-0 grid place-items-center"
              style={{
                background: `conic-gradient(${slColor(sl)} ${Math.min(Math.max(sl, 0), 100)}%, rgb(var(--bg-subtle)) 0)`,
              }}
              aria-hidden
            >
              <span className="w-8 h-8 rounded-full bg-bg grid place-items-center">
                <Target className="w-4 h-4" style={{ color: slColor(sl) }} strokeWidth={2.2} />
              </span>
            </span>
          )}
          <div className="min-w-0">
            <div className="text-[22px] font-bold tabular-nums text-fg leading-none">
              {loading ? <Skeleton className="h-6 w-12" /> : `${Math.round(sl)}%`}
            </div>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-fg-muted mt-1 truncate">
              {t('Nivel de servicio')}
            </div>
            {!loading && (
              <div className="text-[11px] text-fg-subtle mt-0.5 truncate">
                {formatNumberCL(kpis?.serviceLevelBefore || 0)} {t('a tiempo')}
              </div>
            )}
          </div>
        </div>

        <Tile
          icon={Users}
          tone="brand"
          loading={loading}
          value={`${formatNumberCL(activeDrivers)}/${formatNumberCL(totalDrivers)}`}
          label={t('Repartidores activos')}
          sub={t('con GPS en vivo')}
        />
      </div>
    </div>
  );
}
